import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { SearchOutlined, TeamOutlined } from '@ant-design/icons';
import type { KeycloakCandidate, KeycloakUserSource } from '@lens/shared';
import { importKeycloakUsers, previewKeycloakUsers } from './api.ts';
import { spacing, radius, shadows } from '../../shared/theme/tokens.ts';

const { Text } = Typography;

/**
 * Загрузка пользователей из Keycloak (`docs/modules/auth.md §8.8`).
 *
 * Нужна, чтобы завести сотрудников в Lens до их первого входа: иначе их нельзя
 * добавить в пространство. Сначала «Найти» (ничего не меняет), потом «Завести
 * выбранных» — то есть админ всегда видит, что именно произойдёт.
 */

const STATUS_META: Record<
  KeycloakCandidate['status'],
  { label: string; color: string; hint: string; importable: boolean }
> = {
  new: {
    label: 'Новый',
    color: 'green',
    hint: 'В Lens такого пользователя нет — будет заведён',
    importable: true,
  },
  link: {
    label: 'Свяжем',
    color: 'blue',
    hint: 'В Lens есть запись с таким логином или почтой без привязки к Keycloak — свяжем её',
    importable: true,
  },
  exists: {
    label: 'Уже есть',
    color: 'default',
    hint: 'Запись уже привязана к этому аккаунту Keycloak — делать нечего',
    importable: false,
  },
  conflict: {
    label: 'Конфликт',
    color: 'red',
    hint: 'Логин занят записью Lens, привязанной к другому аккаунту Keycloak',
    importable: false,
  },
  disabled: {
    label: 'Выключен',
    color: 'orange',
    hint: 'Пользователь выключен в Keycloak — не заводим',
    importable: false,
  },
};

interface Props {
  /** Реквизиты клиента заполнены целиком (иначе Admin API не опросить). */
  ssoConfigured: boolean;
  /** Есть несохранённые правки формы — сначала «Сохранить». */
  dirty: boolean;
  /** Обновить список пользователей на смежных экранах после загрузки. */
  onImported?: () => void;
}

export function KeycloakUsersCard({ ssoConfigured, dirty, onImported }: Props): JSX.Element {
  const [source, setSource] = useState<KeycloakUserSource>('roles');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [candidates, setCandidates] = useState<KeycloakCandidate[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [roleNames, setRoleNames] = useState<{ access: string; admin: string } | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  // По умолчанию показываем только тех, кого можно завести: в выборке «по
  // ролям» большинство — уже заведённые, и они прятали новых.
  const [showAll, setShowAll] = useState(false);

  const needsQuery = source !== 'roles';

  async function handleSearch(): Promise<void> {
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const res = await previewKeycloakUsers({
        source,
        query: needsQuery ? query.trim() : undefined,
        limit: 200,
      });
      setCandidates(res.candidates);
      setTruncated(res.truncated);
      setWarnings(res.warnings ?? []);
      setRoleNames({ access: res.accessRole, admin: res.adminRole });
      // Предвыбираем только то, что реально можно завести.
      setSelected(
        res.candidates.filter((c) => STATUS_META[c.status].importable).map((c) => c.keycloakId),
      );
      // Пусто и есть предупреждения — причина уже названа в них, красная
      // плашка «никого не нашли» только сбивала бы с толку.
      if (res.candidates.length === 0 && (res.warnings ?? []).length === 0) {
        setError('Keycloak не вернул ни одного пользователя по этому условию');
      }
    } catch (err) {
      setCandidates(null);
      setWarnings([]);
      setError(err instanceof Error ? err.message : 'Не удалось получить список из Keycloak');
    } finally {
      setLoading(false);
    }
  }

  async function handleImport(): Promise<void> {
    if (selected.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      const res = await importKeycloakUsers({
        source,
        query: needsQuery ? query.trim() : undefined,
        limit: 200,
        keycloakIds: selected,
      });
      message.success(`Готово: ${res.created + res.linked} пользователей`);
      onImported?.();
      // Перечитываем — статусы заведённых должны стать «Уже есть». Итог
      // выставляем ПОСЛЕ перечитывания: handleSearch сбрасывает его в начале.
      await handleSearch();
      setSummary(
        `Заведено: ${res.created}, связано: ${res.linked}` +
          (res.skipped > 0 ? `, пропущено: ${res.skipped}` : ''),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось завести пользователей');
    } finally {
      setImporting(false);
    }
  }

  const importableSelected = (candidates ?? []).filter(
    (c) => selected.includes(c.keycloakId) && STATUS_META[c.status].importable,
  ).length;

  const all = candidates ?? [];
  const importableCount = all.filter((c) => STATUS_META[c.status].importable).length;
  const visible = showAll ? all : all.filter((c) => STATUS_META[c.status].importable);

  return (
    <Card
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.sm }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: radius.md,
              background: 'var(--color-primary-bg)',
              color: 'var(--color-primary)',
              fontSize: 14,
            }}
          >
            <TeamOutlined />
          </span>
          Пользователи из Keycloak
        </span>
      }
      style={{ width: '100%', marginBottom: spacing.base, borderRadius: radius.lg, boxShadow: shadows.card }}
    >
      <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
        Заводит сотрудников в Lens до их первого входа — чтобы добавлять их в пространства
        заранее. Пароль таким аккаунтам не задаётся: они входят только через Keycloak.
      </Text>

      {!ssoConfigured && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: spacing.base }}
          message="Сначала заполните реквизиты клиента Keycloak"
          description="Загрузка идёт через Admin API от имени service account того же клиента. Нужны Issuer, Client ID и секрет."
        />
      )}

      {dirty && ssoConfigured && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: spacing.base }}
          message="Сохраните изменения настроек"
          description="Загрузка использует сохранённые реквизиты, а не то, что сейчас в форме."
        />
      )}

      {/* Переключатель источника и кнопка действия — разные вещи, поэтому
          разведены по строкам: три «кнопки» в ряд с четвёртой читались как
          один набор действий. */}
      <div style={{ marginTop: spacing.base }}>
        <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          Кого брать из Keycloak
        </Text>
        <div style={{ marginTop: spacing.xs }}>
          <Segmented<KeycloakUserSource>
            value={source}
            onChange={(value) => {
              setSource(value);
              setCandidates(null);
              setSummary(null);
              setError(null);
            }}
            options={[
              { label: 'Всех с ролью доступа', value: 'roles' },
              { label: 'Участников группы', value: 'group' },
              { label: 'По поиску', value: 'search' },
            ]}
            data-testid="kc-source"
          />
        </div>
        <Text style={{ display: 'block', marginTop: spacing.xs, fontSize: 11, color: 'var(--color-text-secondary)' }}>
          {source === 'roles'
            ? 'Все, кому в Keycloak выдана роль доступа в Lens или роль админа.'
            : source === 'group'
              ? 'Все участники указанной группы Keycloak.'
              : 'Поиск по фамилии, логину или почте — как в консоли Keycloak.'}
        </Text>
      </div>

      <div
        style={{
          display: 'flex',
          gap: spacing.sm,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginTop: spacing.base,
        }}
      >
        {needsQuery && (
          <Input
            placeholder={source === 'group' ? 'Название группы, напр. lens-team' : 'Фамилия, логин или почта'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onPressEnter={() => void handleSearch()}
            style={{ width: 300 }}
            autoComplete="off"
            allowClear
            data-testid="kc-query"
          />
        )}

        <Button
          type="primary"
          icon={<SearchOutlined />}
          loading={loading}
          disabled={!ssoConfigured || (needsQuery && !query.trim())}
          onClick={() => void handleSearch()}
          data-testid="kc-search"
        >
          Показать список
        </Button>
        <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
          Ничего не меняет — сначала покажем, кого нашли.
        </Text>
      </div>

      {source === 'roles' && roleNames && (
        <Text
          style={{ display: 'block', marginTop: spacing.xs, fontSize: 11, color: 'var(--color-text-secondary)' }}
        >
          Взяты обладатели ролей <Text code>{roleNames.access}</Text> и{' '}
          <Text code>{roleNames.admin}</Text>.
        </Text>
      )}
      {source !== 'roles' && candidates && (
        <Text
          style={{ display: 'block', marginTop: spacing.xs, fontSize: 11, color: 'var(--color-text-secondary)' }}
        >
          Роли здесь не проверяются — войти всё равно смогут только обладатели роли доступа.
        </Text>
      )}

      {error && (
        <Alert type="error" showIcon style={{ marginTop: spacing.base }} message={error} />
      )}
      {summary && (
        <Alert type="success" showIcon style={{ marginTop: spacing.base }} message={summary} />
      )}
      {warnings.map((w) => (
        <Alert key={w} type="warning" showIcon style={{ marginTop: spacing.base }} message={w} />
      ))}
      {truncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: spacing.base }}
          message="Показаны первые 200 — в Keycloak есть ещё. Сузьте условие."
        />
      )}

      {candidates && candidates.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              marginTop: spacing.base,
            }}
          >
            <Checkbox
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              data-testid="kc-show-all"
            >
              Показывать уже заведённых
            </Checkbox>
            <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
              {showAll
                ? `Всего найдено: ${all.length}, из них можно завести ${importableCount}`
                : `Можно завести: ${importableCount} из ${all.length} найденных`}
            </Text>
          </div>

          <Table<KeycloakCandidate>
            style={{ marginTop: spacing.sm }}
            size="small"
            rowKey="keycloakId"
            dataSource={visible}
            pagination={visible.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
            data-testid="kc-candidates"
            rowSelection={{
              selectedRowKeys: selected,
              onChange: (keys) => setSelected(keys as string[]),
              getCheckboxProps: (row) => ({ disabled: !STATUS_META[row.status].importable }),
            }}
            columns={[
              { title: 'Логин', dataIndex: 'login', width: 180 },
              { title: 'Имя', dataIndex: 'displayName' },
              {
                title: 'Почта',
                dataIndex: 'email',
                render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
              },
              {
                title: 'Группы',
                dataIndex: 'groups',
                render: (groups: string[] = []) =>
                  groups.length > 0 ? (
                    <Space size={4} wrap>
                      {groups.map((g) => (
                        <Tag key={g}>{g}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
              {
                title: 'Роли',
                dataIndex: 'roles',
                render: (roles: string[]) =>
                  roles.length > 0 ? (
                    <Space size={4} wrap>
                      {roles.map((r) => (
                        <Tag key={r}>{r}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
              {
                title: 'Что будет',
                dataIndex: 'status',
                width: 130,
                render: (status: KeycloakCandidate['status']) => {
                  const meta = STATUS_META[status];
                  return (
                    <Tooltip title={meta.hint}>
                      <Tag color={meta.color}>{meta.label}</Tag>
                    </Tooltip>
                  );
                },
              },
            ]}
          />

          <Button
            type="primary"
            style={{ marginTop: spacing.base }}
            loading={importing}
            disabled={importableSelected === 0}
            onClick={() => void handleImport()}
            data-testid="kc-import"
          >
            Завести выбранных ({importableSelected})
          </Button>
        </>
      )}
    </Card>
  );
}
